import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Box, Input, InputGroup, InputLeftElement, InputRightElement, Text } from '@chakra-ui/react';
import { logicalAndOperator } from '@whitbread-eos/utils';
import { useCombobox, useTagGroup } from 'downshift';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var NOT_GROUPED_KEY = 'notGrouped';
export default function Autocomplete(_ref) {
  var items = _ref.items,
    inputSelectedValue = _ref.inputSelectedValue,
    inputPlaceholder = _ref.inputPlaceholder,
    _ref$multiSelectable = _ref.multiSelectable,
    multiSelectable = _ref$multiSelectable === void 0 ? false : _ref$multiSelectable,
    _ref$openListOnFocus = _ref.openListOnFocus,
    openListOnFocus = _ref$openListOnFocus === void 0 ? true : _ref$openListOnFocus,
    _ref$disableInternalF = _ref.disableInternalFilter,
    disableInternalFilter = _ref$disableInternalF === void 0 ? false : _ref$disableInternalF,
    _ref$isRequired = _ref.isRequired,
    isRequired = _ref$isRequired === void 0 ? false : _ref$isRequired,
    _ref$hasListDivider = _ref.hasListDivider,
    hasListDivider = _ref$hasListDivider === void 0 ? true : _ref$hasListDivider,
    wrapperStyles = _ref.wrapperStyles,
    _ref$icons = _ref.icons,
    icons = _ref$icons === void 0 ? {} : _ref$icons,
    inputStyles = _ref.inputStyles,
    dataTestId = _ref.dataTestId,
    onChange = _ref.onChange,
    onSelectOption = _ref.onSelectOption,
    onInputChange = _ref.onInputChange,
    onBlurInput = _ref.onBlurInput,
    onFocusInput = _ref.onFocusInput,
    setInputHasFocus = _ref.setInputHasFocus,
    ariaInvalid = _ref.ariaInvalid,
    ariaDescribedBy = _ref.ariaDescribedBy,
    _ref$showElements = _ref.showElements,
    showElements = _ref$showElements === void 0 ? true : _ref$showElements,
    listStyles = _ref.listStyles,
    _ref$hasItemObject = _ref.hasItemObject,
    hasItemObject = _ref$hasItemObject === void 0 ? false : _ref$hasItemObject,
    isPriceFinder = _ref.isPriceFinder;
  var inputRef = useRef(null);
  var autocompleteListRef = useRef(null);
  var comboboxRef = useRef(null);
  var justSelectedRef = useRef(false);
  var itemWasSelectedRef = useRef(false);
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    internalInputValue = _useState2[0],
    setInternalInputValue = _useState2[1];
  var currentInputValue = inputSelectedValue !== undefined ? inputSelectedValue : internalInputValue;
  var groupedItems = items.reduce((acc, cur) => {
    var _cur$group = cur.group,
      group = _cur$group === void 0 ? NOT_GROUPED_KEY : _cur$group;
    acc[group] = acc[group] || [];
    acc[group].push(cur);
    return acc;
  }, {});
  var flatItems = items;
  var filteredItems = disableInternalFilter ? flatItems : flatItems.filter(item => item.value.toLowerCase().includes(currentInputValue.toLowerCase()));
  var _useCombobox = useCombobox(_objectSpread({
      items: filteredItems,
      inputValue: currentInputValue,
      onInputValueChange: _ref2 => {
        var inputValue = _ref2.inputValue,
          type = _ref2.type;
        if (!multiSelectable) {
          if (type === useCombobox.stateChangeTypes.InputChange) {
            itemWasSelectedRef.current = false;
          }
          setInternalInputValue(inputValue || '');
          onInputChange === null || onInputChange === void 0 || onInputChange(inputValue);
        }
      },
      onSelectedItemChange: _ref3 => {
        var selectedItem = _ref3.selectedItem;
        if (!multiSelectable && selectedItem) {
          justSelectedRef.current = true;
          itemWasSelectedRef.current = true;
          var paramsItem = hasItemObject ? {
            label: selectedItem.value,
            originalValue: selectedItem,
            value: selectedItem.value
          } : selectedItem.value;
          onSelectOption === null || onSelectOption === void 0 || onSelectOption(paramsItem);
          var itemObject = {
            label: selectedItem.value,
            value: selectedItem.value,
            originalValue: selectedItem.value
          };
          onChange === null || onChange === void 0 || onChange(selectedItem.value, itemObject);
        }
      },
      itemToString: item => item ? item.value : ''
    }, openListOnFocus ? {} : {
      defaultIsOpen: false
    })),
    comboboxIsOpen = _useCombobox.isOpen,
    getMenuProps = _useCombobox.getMenuProps,
    getInputProps = _useCombobox.getInputProps,
    comboboxHighlightedIndex = _useCombobox.highlightedIndex,
    comboboxGetItemProps = _useCombobox.getItemProps,
    comboboxSelectedItem = _useCombobox.selectedItem,
    openMenu = _useCombobox.openMenu,
    closeMenu = _useCombobox.closeMenu,
    toggleMenu = _useCombobox.toggleMenu,
    reset = _useCombobox.reset,
    selectItem = _useCombobox.selectItem;
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedTags = _useState4[0],
    setSelectedTags = _useState4[1];
  var _useTagGroup = useTagGroup({
      items: selectedTags,
      onItemsChange: _ref4 => {
        var newItems = _ref4.items;
        if (multiSelectable && newItems) {
          setSelectedTags(newItems);
          onChange === null || onChange === void 0 || onChange(newItems.map(item => item.value).join(',') || '');
        }
      }
    }),
    tagItems = _useTagGroup.items,
    addTagItem = _useTagGroup.addItem;
  var removeTagItem = item => {
    setSelectedTags(prev => prev.filter(t => t.value !== item.value));
  };
  var tagGroupHook = useTagGroup({
    items: tagItems
  });
  var _useCombobox2 = useCombobox({
      items: filteredItems,
      inputValue: currentInputValue,
      selectedItem: null,
      onInputValueChange: _ref5 => {
        var inputValue = _ref5.inputValue;
        if (multiSelectable) {
          setInternalInputValue(inputValue || '');
          onInputChange === null || onInputChange === void 0 || onInputChange(inputValue);
        }
      },
      onSelectedItemChange: _ref6 => {
        var selectedItem = _ref6.selectedItem;
        if (multiSelectable && selectedItem) {
          addTagItem(selectedItem);
          var paramsItem = hasItemObject ? selectedItem : selectedItem.value;
          onSelectOption === null || onSelectOption === void 0 || onSelectOption(paramsItem);
          setInternalInputValue('');
        }
      },
      itemToString: item => item ? item.value : '',
      stateReducer: (state, actionAndChanges) => {
        var changes = actionAndChanges.changes,
          type = actionAndChanges.type;
        switch (type) {
          case useCombobox.stateChangeTypes.InputKeyDownEnter:
          case useCombobox.stateChangeTypes.ItemClick:
            return _objectSpread(_objectSpread({}, changes), {}, {
              isOpen: true,
              highlightedIndex: state.highlightedIndex,
              inputValue: ''
            });
          case useCombobox.stateChangeTypes.InputBlur:
            return _objectSpread(_objectSpread({}, changes), {}, {
              inputValue: ''
            });
          default:
            return changes;
        }
      }
    }),
    multiComboboxIsOpen = _useCombobox2.isOpen,
    multiGetMenuProps = _useCombobox2.getMenuProps,
    multiGetInputProps = _useCombobox2.getInputProps,
    multiHighlightedIndex = _useCombobox2.highlightedIndex,
    multiGetItemProps = _useCombobox2.getItemProps,
    multiReset = _useCombobox2.reset,
    multiOpenMenu = _useCombobox2.openMenu,
    multiCloseMenu = _useCombobox2.closeMenu,
    multiToggleMenu = _useCombobox2.toggleMenu;
  useImperativeHandle(comboboxRef, () => ({
    open: multiSelectable ? multiOpenMenu : openMenu,
    close: multiSelectable ? multiCloseMenu : closeMenu,
    toggle: multiSelectable ? multiToggleMenu : toggleMenu,
    resetItems: () => {
      if (multiSelectable) {
        multiReset();
        tagItems.forEach(() => removeTagItem(tagItems[0]));
      } else {
        reset();
      }
      setInternalInputValue('');
      itemWasSelectedRef.current = false;
    },
    removeItem: value => {
      if (multiSelectable) {
        var item = tagItems.find(item => item.value === value);
        if (item) {
          removeTagItem(item);
        }
      } else {
        selectItem(null);
      }
    }
  }));
  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    var isInputFocused = typeof document !== 'undefined' && document.activeElement === inputRef.current;
    if (filteredItems.length > 0 && currentInputValue.length > 0 && isInputFocused) {
      if (multiSelectable) {
        multiOpenMenu();
      } else {
        openMenu();
      }
    } else if (filteredItems.length === 0) {
      if (multiSelectable) {
        multiCloseMenu();
      } else {
        closeMenu();
      }
    }
  }, [filteredItems.length, items.length]);
  var isOpen = multiSelectable ? multiComboboxIsOpen : comboboxIsOpen;
  var highlightedIndex = multiSelectable ? multiHighlightedIndex : comboboxHighlightedIndex;
  var selectedItem = multiSelectable ? null : comboboxSelectedItem;
  var autocompleteState = {
    isOpen,
    inputValue: currentInputValue,
    selectedItem,
    highlightedIndex
  };
  var menuProps = multiSelectable ? multiGetMenuProps({}, {
    suppressRefError: true
  }) : getMenuProps({}, {
    suppressRefError: true
  });
  var finalMenuProps = _objectSpread(_objectSpread({}, menuProps), {}, {
    role: 'dialog'
  });
  if (!multiSelectable) {
    multiGetInputProps({}, {
      suppressRefError: true
    });
    multiGetMenuProps({}, {
      suppressRefError: true
    });
  } else {
    getInputProps({}, {
      suppressRefError: true
    });
    getMenuProps({}, {
      suppressRefError: true
    });
  }
  return _jsx(Box, {
    as: "span",
    children: _jsxs(Box, {
      style: {
        height: '100%'
      },
      children: [_jsx(InputGroup, _objectSpread(_objectSpread({
        "data-testid": "".concat(dataTestId, "-inputGroup")
      }, wrapperStyles), {}, {
        children: multiSelectable ? renderMultipleInput(inputPlaceholder) : renderSimpleInput(autocompleteState, inputPlaceholder, dataTestId)
      })), _jsxs(Box, _objectSpread(_objectSpread(_objectSpread(_objectSpread({
        as: "ul"
      }, autocompleteListStyles), listStyles), {}, {
        w: isPriceFinder ? autocompleteListWidthPriceFinder : autocompleteListWidth,
        ref: autocompleteListRef,
        "data-testid": "".concat(dataTestId, "-autocompleteList")
      }, finalMenuProps), {}, {
        style: {
          display: filteredItems.length > 0 && isOpen ? 'block' : 'none',
          zIndex: 999
        },
        children: [isPriceFinder && Object.entries(groupedItems).map(_ref7 => {
          var _ref8 = _slicedToArray(_ref7, 2),
            key = _ref8[0],
            values = _ref8[1];
          if (key === NOT_GROUPED_KEY) {
            return values.map((v, id) => {
              var index = filteredItems.findIndex(item => item.value === v.value);
              return renderItem(id, v, index);
            });
          }
          return null;
        }), !isPriceFinder && (() => {
          var renderedElements = [];
          Object.entries(groupedItems).forEach((_ref9, groupId) => {
            var _ref0 = _slicedToArray(_ref9, 2),
              key = _ref0[0],
              values = _ref0[1];
            if (key !== NOT_GROUPED_KEY && showElements) {
              renderedElements.push(_jsx(Box, _objectSpread(_objectSpread({
                "data-testid": "".concat(dataTestId, "-hotelsLabel"),
                as: "div"
              }, autocompleteGroupTitleStyles), {}, {
                sx: {
                  listStyle: 'none'
                },
                children: key
              }), "group-title-".concat(groupId)));
            }
            values.forEach((v, idx) => {
              var index = filteredItems.findIndex(item => item.value === v.value);
              var itemKey = parseInt("".concat(groupId).concat(idx), 10);
              renderedElements.push(renderItem(itemKey, v, index));
            });
            if (key !== NOT_GROUPED_KEY && hasListDivider && showElements && groupId < Object.entries(groupedItems).length - 1) {
              renderedElements.push(_jsx(Box, {
                as: "hr",
                borderColor: "lightGrey3",
                my: 0,
                sx: {
                  listStyle: 'none'
                }
              }, "divider-".concat(groupId)));
            }
          });
          return renderedElements;
        })()]
      }))]
    })
  });
  function renderItem(key, item, itemIndex) {
    var getItemPropsFunc = multiSelectable ? multiGetItemProps : comboboxGetItemProps;
    var isHighlighted = highlightedIndex === itemIndex;
    return _jsxs(Box, _objectSpread(_objectSpread(_objectSpread(_objectSpread({
      as: "li"
    }, autocompleteListItemStyles), isHighlighted ? {
      bg: 'var(--chakra-colors-lightGrey5) !important'
    } : {}), getItemPropsFunc({
      item,
      index: itemIndex
    })), {}, {
      sx: {
        listStyle: 'none'
      },
      children: [item.component, _jsx(Text, _objectSpread(_objectSpread({
        as: "span"
      }, autocompleteItemValueStyles), {}, {
        children: item.value
      }))]
    }), key);
  }
  function renderMultipleInput(inputPlaceholder) {
    var localGetTagGroupProps = tagGroupHook.getTagGroupProps,
      localGetTagProps = tagGroupHook.getTagProps,
      localGetTagRemoveProps = tagGroupHook.getTagRemoveProps;
    var multiInputProps = multiGetInputProps({
      ref: inputRef,
      onFocus: () => {
        onFocusInput === null || onFocusInput === void 0 || onFocusInput();
        setInputHasFocus === null || setInputHasFocus === void 0 || setInputHasFocus(true);
      },
      onBlur: () => {
        setInputHasFocus === null || setInputHasFocus === void 0 || setInputHasFocus(false);
      }
    });
    return _jsxs(_Fragment, {
      children: [_jsx(Box, _objectSpread(_objectSpread({}, localGetTagGroupProps()), {}, {
        display: "flex",
        flexWrap: "wrap",
        gap: "xs",
        children: tagItems.map((tag, index) => _jsxs(Box, _objectSpread(_objectSpread({
          as: "span",
          bg: "gray.100",
          borderRadius: "md",
          px: "2",
          py: "1",
          display: "inline-flex",
          alignItems: "center"
        }, localGetTagProps({
          index
        })), {}, {
          children: [tag.value, _jsx(Box, _objectSpread(_objectSpread({
            as: "button",
            ml: "1",
            cursor: "pointer",
            fontSize: "sm"
          }, localGetTagRemoveProps({
            index
          })), {}, {
            onClick: e => {
              e.stopPropagation();
              removeTagItem(tag);
            },
            children: "\xD7"
          }))]
        }), "".concat(tag.value, "-").concat(index)))
      })), _jsx(Input, _objectSpread(_objectSpread(_objectSpread({}, inputStyles), multiInputProps), {}, {
        placeholder: inputPlaceholder,
        "data-testid": "".concat(dataTestId, "-locationPlaceholder"),
        isRequired: isRequired,
        isInvalid: ariaInvalid,
        value: currentInputValue,
        onChange: event => {
          var _multiInputProps$onCh, _event$target;
          (_multiInputProps$onCh = multiInputProps.onChange) === null || _multiInputProps$onCh === void 0 || _multiInputProps$onCh.call(multiInputProps, event);
          onInputChange === null || onInputChange === void 0 || onInputChange(event === null || event === void 0 || (_event$target = event.target) === null || _event$target === void 0 ? void 0 : _event$target.value);
        },
        "aria-describedby": ariaDescribedBy || undefined
      }))]
    });
  }
  function renderSimpleInput(autocompleteState, inputPlaceholder, dataTestId) {
    var createIcon = (icon, showLeftIcon) => {
      if (!icon || logicalAndOperator(icon === null, !showLeftIcon)) {
        return null;
      }
      var PrimaryIcon = icon;
      return _jsx(PrimaryIcon, _objectSpread(_objectSpread({}, autocompleteState), {}, {
        inputPlaceholder: inputPlaceholder,
        autocompleteRef: comboboxRef,
        autocompleteInputRef: inputRef
      }));
    };
    var leftIcon = createIcon(icons === null || icons === void 0 ? void 0 : icons.left, showElements);
    var rightIcon = createIcon(icons === null || icons === void 0 ? void 0 : icons.right);
    var comboboxInputProps = getInputProps({
      ref: inputRef,
      onFocus: () => {
        onFocusInput === null || onFocusInput === void 0 || onFocusInput();
        setInputHasFocus === null || setInputHasFocus === void 0 || setInputHasFocus(true);
        if (openListOnFocus) {
          openMenu();
        }
      },
      onBlur: event => {
        var _inputRef$current, _inputRef$current2, _event$relatedTarget, _inputRef$current3;
        setInputHasFocus === null || setInputHasFocus === void 0 || setInputHasFocus(false);
        if (itemWasSelectedRef.current) {
          return;
        }
        var list = Object.entries(groupedItems);
        if (inputRef !== null && inputRef !== void 0 && (_inputRef$current = inputRef.current) !== null && _inputRef$current !== void 0 && _inputRef$current.value && (inputRef === null || inputRef === void 0 || (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 || (_inputRef$current2 = _inputRef$current2.value) === null || _inputRef$current2 === void 0 ? void 0 : _inputRef$current2.length) >= 3 && list !== null && list !== void 0 && list.length && ((_event$relatedTarget = event.relatedTarget) === null || _event$relatedTarget === void 0 || (_event$relatedTarget = _event$relatedTarget.parentNode) === null || _event$relatedTarget === void 0 ? void 0 : _event$relatedTarget.parentNode) !== (inputRef === null || inputRef === void 0 || (_inputRef$current3 = inputRef.current) === null || _inputRef$current3 === void 0 || (_inputRef$current3 = _inputRef$current3.parentNode) === null || _inputRef$current3 === void 0 ? void 0 : _inputRef$current3.parentNode)) {
          var _list$ = _slicedToArray(list[0], 2),
            values = _list$[1];
          if (values !== null && values !== void 0 && values.length) {
            var item = values[0];
            onBlurInput === null || onBlurInput === void 0 || onBlurInput(item.value);
          }
        }
      }
    });
    return _jsxs(_Fragment, {
      children: [_jsx(Input, _objectSpread(_objectSpread(_objectSpread({}, renderStyles()), comboboxInputProps), {}, {
        placeholder: inputPlaceholder,
        "data-testid": "".concat(dataTestId, "-locationPlaceholder"),
        isRequired: isRequired,
        isInvalid: ariaInvalid,
        value: currentInputValue,
        onChange: event => {
          var _comboboxInputProps$o, _event$target2;
          (_comboboxInputProps$o = comboboxInputProps.onChange) === null || _comboboxInputProps$o === void 0 || _comboboxInputProps$o.call(comboboxInputProps, event);
          onInputChange === null || onInputChange === void 0 || onInputChange(event === null || event === void 0 || (_event$target2 = event.target) === null || _event$target2 === void 0 ? void 0 : _event$target2.value);
        },
        "aria-describedby": ariaDescribedBy || undefined
      })), leftIcon !== null && _jsx(InputLeftElement, {
        top: "50%",
        transform: "auto",
        translateY: "-50%",
        children: leftIcon
      }), rightIcon && _jsx(InputRightElement, {
        top: "50%",
        transform: "auto",
        translateY: "-50%",
        translateX: isPriceFinder ? '-150%' : '0',
        "data-testid": "".concat(dataTestId, "-inputRight"),
        children: rightIcon
      })]
    });
  }
  function renderStyles() {
    return _objectSpread(_objectSpread(_objectSpread({}, icons !== null && icons !== void 0 && icons.left ? {
      pl: '2xl'
    } : {}), inputStyles), isPriceFinder ? {
      paddingRight: '65px'
    } : {});
  }
}
var autocompleteListWidth = {
  mobile: '18rem',
  xs: '21.4375rem',
  sm: '23.375rem',
  lg: '25.25rem',
  xl: '26.25rem'
};
var autocompleteListWidthPriceFinder = {
  mobile: 'full',
  md: '25.25rem',
  xl: '26.25rem'
};
var autocompleteListStyles = {
  position: 'absolute',
  bg: 'var(--chakra-colors-white)',
  border: '1px solid var(--chakra-colors-lightGrey3)',
  borderRadius: 'var(--chakra-space-radiusSmall)',
  boxShadow: '0 2px var(--chakra-space-xmd) var(--chakra-colors-lightGrey2)',
  marginTop: 0,
  maxHeight: '28.75rem',
  overflow: 'hidden',
  py: 'sm',
  ml: '0 !important'
};
var autocompleteGroupTitleStyles = {
  fontSize: 'sm',
  lineHeight: '2',
  fontWeight: 'normal',
  margin: 'var(--chakra-space-xmd) 0 var(--chakra-space-sm) var(--chakra-space-md)',
  color: 'darkGrey1',
  textTransform: 'capitalize'
};
var autocompleteListItemStyles = {
  display: 'flex',
  alignItems: 'center',
  h: 'var(--chakra-space-2xl)',
  fontSize: 'sm',
  lineHeight: '1',
  fontWeight: 'normal',
  color: 'darkGrey1',
  mx: 0,
  p: 'var(--chakra-space-xmd) var(--chakra-space-md)',
  borderRadius: 0,
  bg: 'var(--chakra-colors-baseWhite) !important',
  cursor: 'pointer',
  _hover: {
    bg: 'var(--chakra-colors-lightGrey5) !important'
  }
};
var autocompleteItemValueStyles = {
  display: 'block',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap'
};